import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { ClearOutlined } from '@mui/icons-material';
import Deleteicon from '@mui/icons-material/DeleteForeverOutlined';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import MarkEmailUnreadOutlinedIcon from '@mui/icons-material/MarkEmailUnreadOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import SearchIcon from '@mui/icons-material/Search';
import { Stack, createTheme, useMediaQuery } from '@mui/material';
import { Typography, Box, InputBase, IconButton, Badge, Tooltip } from '@mui/material';
import moment from 'moment/moment';
import { CustomizedMenus, CustomizedTable, Loader, CustomPagination } from 'app/components/v2';
import {
  ParentDiv,
  TitleContainer,
  Title,
  TableHeader,
  CustomDiv,
  CustomHeaderDiv1,
  CustomHeaderDiv2,
  TableDiv,
  scrollbarStyle,
  FilterDiv,
} from './style';
import './style.css';
import { pushID } from 'app/store/features/notificationSlice';
import {
  getAllMessages,
  updateMultipleMessage,
  toggleRead,
  deleteMultipleMessage,
} from 'app/store/features/notificationsSlice';
import { ColorsV2 } from 'app/constants/color';
// import DeleteIcon from 'app/components/Svgs/DeleteIcon';
// import MarkAsReadIcon from 'app/components/Svgs/MarkAsReadIcon';
// import MarkAsUnreadIcon from 'app/components/Svgs/MarkAsUnreadIcon';
import { Buttons } from 'app/constants/sizing';
import apiService from 'app/services/apiService';
import Message from './MessageFormatter';

// eslint-disable-next-line complexity
const NotificationList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('translation', { keyPrefix: 'messages_headings' });
  const [mlist, setMlist] = useState([]);
  const [showFilter, setShowFilter] = useState(false);
  const [filter, setFilter] = useState('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState([]);
  const [selectedRow, setSelectedRow] = useState([]);
  const [pageSize, setPageSize] = useState(10);
  const [pageNo, setPageNo] = useState(0);
  const [messageBodies, setMessageBodies] = useState({});
  const [noRowTxt, setNoRowText] = useState(t('noRows'));
  const gridRef = useRef(null);
  const storeNotifications = useSelector(state => state.notifications.messages);
  const isDataFetched = useSelector(state => state.notifications.isDataFetched);
  const isLoading = useSelector(state => state.loading.isLoading);
  const Row = useSelector(state => state.notification.id);
  // const currentLang = JSON.parse(localStorage.getItem('lang'));

  const message = 'No Message Content';
  const fetchData = useCallback(() => {
    if (!isDataFetched) {
      dispatch(getAllMessages());
    }
  }, []);
  useEffect(() => {
    setSelectedRow([Row]);

    let index = 0;

    for (let i = 0; i < mlist.length; i++) {
      if (mlist[i].id === Row) {
        index = i + 1;
        break;
      }
    }

    const count = Math.ceil(index / pageSize);
    Row !== '' && setPageNo(count === 0 ? count : count - 1);
  }, [Row]);

  const updateMessageStatus = (id, read) => {
    const payload = { read: !read, messages: [id] };
    dispatch(updateMultipleMessage(payload));
    const filtered = storeNotifications?.map(item =>
      item.id === id ? { ...item, read: !read } : item
    );
    dispatch(toggleRead(filtered));
  };

  const handleUpdateMessages = action => {
    const selectedMessages = selected?.slice(
      pageNo === 0 ? 0 : pageSize * pageNo,
      pageNo === 0 ? pageSize : pageSize * pageNo + pageSize
    );
    const payload = {
      read: action,
      messages: selectedMessages,
    };
    dispatch(updateMultipleMessage(payload));
    const updatedMlist = storeNotifications?.map(row => {
      if (selectedMessages.includes(row.id)) {
        return { ...row, read: action };
      }
      return row;
    });
    dispatch(toggleRead(updatedMlist));
    setSelected([]);
    gridRef.current.setSelectionModel([]);
  };

  const handleReadMessages = id => {
    if (id !== '') {
      const payload = { read: true, messages: [id] };
      dispatch(updateMultipleMessage(payload));
      const filtered = storeNotifications?.map(item =>
        item.id === id ? { ...item, read: true } : item
      );
      dispatch(toggleRead(filtered));
    }
  };
  const handleDelete = id => {
    const payload = { messages: [id] };
    dispatch(deleteMultipleMessage(payload));
    const filtered = storeNotifications?.filter(item => item.id !== id);
    dispatch(toggleRead(filtered));
  };

  const handleMultipleDelete = () => {
    const selectedMessages = selected?.slice(
      pageNo === 0 ? 0 : pageSize * pageNo,
      pageNo === 0 ? pageSize : pageSize * pageNo + pageSize
    );
    const payload = {
      messages: selectedMessages,
    };
    dispatch(deleteMultipleMessage(payload));
    const filtered = storeNotifications?.filter(row => !selectedMessages.includes(row.id));
    dispatch(toggleRead(filtered));
  };

  const handleSearch = event => {
    setSearch(event.target.value);
  };

  const formatTime = array => {
    return array?.map(item => ({
      ...item,
      time: moment(item.time, 'YYY-MM-DD h:mm:ss A').format('DD MMM YY HH:mm'),
    }));
  };

  const filteredList = formatTime(mlist)?.filter(item => {
    return (
      item.time.toLowerCase().includes(search.toLowerCase()) ||
      item.header.toLowerCase().includes(search.toLowerCase())
    );
  });

  const fetchUnreadMessages = status => {
    setShowFilter(true);
    setFilter(status ? t('showing_read') : t('showing_unread'));
    setNoRowText(status ? t('noreadRows') : t('nounreadRows'));
    const filteredReadMessages = storeNotifications?.filter(items => items.read === true);
    const filteredUnReadMessages = storeNotifications?.filter(items => items.read !== true);

    status ? setMlist(filteredReadMessages) : setMlist(filteredUnReadMessages);
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    setMlist(storeNotifications);
  }, [storeNotifications]);

  useEffect(() => {
    if (location.state) {
      setSelectedRow([location.state]);
      if (isDataFetched) {
        const index = mlist.findIndex(item => item.id === location.state);
        const updatedIndex = index + 1;
        if (index !== -1) {
          const count = Math.ceil(updatedIndex / pageSize);
          setPageNo(!count ? count : count - 1);
        }
        ``;
      }
    }
  }, [location.state, isDataFetched]);

  useEffect(() => {
    if (selectedRow?.length === 0) {
      return;
    }
    selectedRow?.forEach(id => {
      if (messageBodies[id]) {
        return;
      }
      apiService
        .getMessageBody(id)
        .then(resp => {
          if (resp.data.data && resp.data.status === 'success') {
            setMessageBodies(prev => ({ ...prev, [id]: resp.data.data.content }));
          }
        })
        .catch(error => {
          console.error(error);
        });
    });
    handleReadMessages(...selectedRow);
  }, [selectedRow]);

  const theme = createTheme({
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 900,
        lg: 1200,
        xl: 1536,
        mobile: 0,
        tablet: 640,
        laptop: 1024,
        desktop: 1350,
      },
    },
  });

  const matchesSmall = useMediaQuery(theme.breakpoints.up('sm'));
  const matchesMedium = useMediaQuery(theme.breakpoints.up('md'));
  const matchesLarge = useMediaQuery(theme.breakpoints.up('lg'));
  const matchesExtraLarge = useMediaQuery(theme.breakpoints.up('xl'));
  const matchesLaptop = useMediaQuery(theme.breakpoints.up('laptop'));
  const matchesDesktop = useMediaQuery(theme.breakpoints.up('desktop'));

  const currentDate = new Date();

  const formattedDateString = moment(currentDate, 'YYY-MM-DD').format('DD MMM YY');

  const notificationColumns = [
    {
      field: 'time',
      headerName: t('heading_1'),
      minWidth: matchesExtraLarge
        ? 100
        : matchesDesktop
        ? 100
        : matchesLarge
        ? 100
        : matchesLaptop
        ? 100
        : matchesMedium
        ? 100
        : matchesSmall
        ? 100
        : 80,
      align: 'left',
      headerAlign: 'left',
      flex: 0.2,

      // sortingOrder: ['desc', 'asc'],
      renderCell: params => (
        <div style={{ height: '100%', padding: '30px 0px 0px 0px' }}>
          {params.row.read === false ? (
            // <div
            //   style={{
            //     fontFamily: 'Roboto, sans-serif',
            //     fontStyle: 'normal',
            //     fontWeight: 700,
            //     fontSize: '0.7em',
            //     lineHeight: '21px',
            //     marginTop: '0.1em',
            //     // borderBottom: `1px solid ${Colors.outline}`,
            //     color: ColorsV2.white,
            //   }}
            // >
            //   {selectedRow.includes(params.row.id) ? (
            //     <>
            //       {params.row.time.slice(0, 9)}
            //       <br />
            //       {params.row.time.slice(10, 15)}
            //     </>
            //   ) : formattedDateString === params.row.time.slice(0, 9) ? (
            //     params.row.time.slice(10, 15)
            //   ) : (
            //     params.row.time.slice(0, 6)
            //   )}
            // </div>
            <Typography variant='body2' sx={{ color: ColorsV2.textInfo }}>
              {selectedRow.includes(params.row.id) ? (
                <>
                  {params.row.time.slice(0, 9)}
                  <br />
                  {params.row.time.slice(10, 15)}
                </>
              ) : formattedDateString === params.row.time.slice(0, 9) ? (
                params.row.time.slice(10, 15)
              ) : (
                params.row.time.slice(0, 6)
              )}
            </Typography>
          ) : (
            // <div
            //   style={{
            //     fontFamily: 'Roboto, sans-serif',
            //     fontStyle: 'normal',
            //     fontWeight: 500,
            //     fontSize: '0.7em',
            //     lineHeight: '21px',
            //     marginTop: '0.1em',
            //     color: ColorsV2.white,
            //   }}
            // >
            //   {selectedRow.includes(params.row.id) ? (
            //     <>
            //       {params.row.time.slice(0, 9)}
            //       <br />
            //       {params.row.time.slice(10, 15)}
            //     </>
            //   ) : formattedDateString === params.row.time.slice(0, 9) ? (
            //     params.row.time.slice(10, 15)
            //   ) : (
            //     params.row.time.slice(0, 6)
            //   )}
            // </div>
            <Typography variant='body2' sx={{ color: ColorsV2.textInfo }}>
              {selectedRow.includes(params.row.id) ? (
                <>
                  {params.row.time.slice(0, 9)}
                  <br />
                  {params.row.time.slice(10, 15)}
                </>
              ) : formattedDateString === params.row.time.slice(0, 9) ? (
                params.row.time.slice(10, 15)
              ) : (
                params.row.time.slice(0, 6)
              )}
            </Typography>
          )}
        </div>
      ),
    },
    {
      field: 'header',
      headerName: t('heading_2'),
      minWidth: matchesExtraLarge
        ? 750
        : matchesDesktop
        ? 750
        : matchesLarge
        ? 650
        : matchesLaptop
        ? 500
        : matchesMedium
        ? 500
        : matchesSmall
        ? 450
        : 400,
      flex: 2,
      sortable: false,
      renderCell: params => (
        <div
          className='messageContentContainer'
          style={{
            ...scrollbarStyle,
            height: '100%',
            // marginTop: '-5vh',
            overflowY:
              selectedRow.includes(params.row.id) && messageBodies[params.row.id]?.length > 100
                ? 'scroll'
                : 'hidden',
            width: '100%',
            wordWrap: 'break-word',
            overflowX: 'hidden',
          }}
        >
          <div style={{ width: '80%', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Typography
              variant='subtitle1'
              sx={{
                // lineHeight: '21px',
                padding: '10px 0px 10px 0px',
                cursor: 'pointer',
                color: params.row.read ? ColorsV2.textInfo : ColorsV2.white,
                width: '80%',
                whiteSpace: selectedRow.includes(params.row.id) && 'pre-wrap',
                overflowWrap: selectedRow.includes(params.row.id) && 'break-word',
                textOverflow: !selectedRow.includes(params.row.id) && 'ellipsis',
                overflow: !selectedRow.includes(params.row.id) && 'hidden',
                // fontWeight: params.row.read ? 400 : 700
              }}
              onClick={() => {
                // selectedRow.includes(params.row.id) &&
                //   setSelectedRow(selectedRow.filter(item => item !== params.row.id));
                setSelectedRow(prev =>
                  prev.includes(params.row.id)
                    ? prev.filter(item => item !== params.row.id)
                    : [params.row.id]
                );
                navigate('/messages', { state: null });
                handleReadMessages(params.row.id);
                dispatch(pushID(''));
              }}
            >
              {params.row.header !== '' ? params.row.header : '<No Subject>'}
            </Typography>
          </div>

          {/* )} */}
          {messageBodies[params.row.id] && selectedRow.includes(params.row.id) && (
            <div style={{ padding: '10px 0px', width: '90%' }}>
              <Message
                message={
                  messageBodies[params.row.id] === '' ? message : messageBodies[params.row.id]
                }
                sx={{
                  // fontFamily: 'Roboto, sans-serif',
                  // fontStyle: 'normal',
                  // fontWeight: 400,
                  // fontSize: '1rem',
                  // lineHeight: '26px',
                  color: ColorsV2.white,
                  // height: '100%',
                  // width: '100%',
                  // whiteSpace: 'pre-wrap',
                  // overflowWrap: 'break-word',
                }}
              ></Message>
              {/* <p
                className='message'
                dangerouslySetInnerHTML={{
                  __html:
                    messageBodies[params.row.id] === '' ? message : (messageBodies[params.row.id]),
                }}
                style={{
                  fontFamily: 'Roboto, sans-serif',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  fontSize: '1rem',
                  lineHeight: '26px',
                  color: ColorsV2.white,
                  height: '100%',
                  width: '100%',
                  whiteSpace: 'pre-wrap',
                  overflowWrap: 'break-word',
                }}
              /> */}
            </div>
          )}
        </div>
      ),
    },
    {
      field: 'action',
      headerName: t('heading_3'),
      minWidth: matchesExtraLarge
        ? 200
        : matchesDesktop
        ? 200
        : matchesLarge
        ? 100
        : matchesLaptop
        ? 100
        : matchesMedium
        ? 100
        : matchesSmall
        ? 100
        : 100,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      flex: 0.1,
      renderCell: params => (
        <div
          style={{
            margin: '0px 0.8em',
            display: 'flex',
            alignItems: 'start',
            justifyContent: 'space-betweeen',
            height: '100%',
            padding: '0.8em 0px 0px 0px',
          }}
        >
          <Tooltip title={t('delete_message')}>
            <IconButton
              style={{
                ...Buttons.baseStyle.iconButton,
                color: ColorsV2.white,
              }}
              onClick={() => handleDelete(params.row.id)}
            >
              <Deleteicon></Deleteicon>
            </IconButton>
          </Tooltip>

          <Tooltip title={params.row.read ? t('mark_as_unread') : t('mark_as_read')}>
            <IconButton
              style={{
                ...Buttons.baseStyle.iconButton,
                color: ColorsV2.white,
              }}
              onClick={() => {
                {
                  params.row.read === true && setSelectedRow([]);
                  params.row.read === true && setMessageBodies({});
                }
                updateMessageStatus(params.row.id, params.row.read);
              }}
            >
              {/* Swap icons for MUI icons */}
              {params.row.read === true ? (
                <MarkEmailUnreadOutlinedIcon></MarkEmailUnreadOutlinedIcon>
              ) : (
                <MarkEmailReadIcon></MarkEmailReadIcon>
              )}
            </IconButton>
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
      <ParentDiv sx={{ width: '100%', marginTop: '7%', marginLeft: '10px' }}>
        <TitleContainer paddingBottom={1}>
          <Title>{t('main')}</Title>
        </TitleContainer>
        <TableHeader
          sx={{
            backgroundColor: ColorsV2.secondary,
            border: `1px solid ${ColorsV2.outline}`,
            borderRadius: '0',
          }}
        >
          <CustomDiv>
            <CustomHeaderDiv1>
              <Typography
                variant='h6'
                sx={{
                  color: ColorsV2.white,
                  marginRight: '10px',
                  fontFamily: 'DM Sans',
                  fontStyle: 'normal',
                  fontSize: '0.9em',
                  fontWeight: 700,
                }}
              >
                {t('all')} ({filteredList?.length ? filteredList.length : 0})
              </Typography>
              <Box
                sx={{
                  border: `2px solid ${ColorsV2.white}`,
                  borderRadius: '5px',
                  height: '1.6em',
                  width: '13vw',
                  marginLeft: '10px',
                  display: 'flex',
                  background: 'transparent',
                }}
              >
                <InputBase
                  sx={{ ml: 1, flex: 1, color: ColorsV2.white, fontSize: '1rem' }}
                  placeholder={t('search')}
                  inputProps={{ 'aria-label': 'search' }}
                  value={search}
                  onChange={event => handleSearch(event)}
                />
                {search === '' ? (
                  <IconButton
                    type='button'
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                    aria-label='search'
                  >
                    <SearchIcon
                      sx={{
                        // ...Buttons.baseStyle.iconButton,
                        color: ColorsV2.white,
                        fontSize: '1rem',
                        '&:hover': { color: ColorsV2.acent },
                      }}
                    />
                  </IconButton>
                ) : (
                  <IconButton
                    type='button'
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                    aria-label='search'
                    onClick={() => setSearch('')}
                  >
                    <ClearOutlined
                      sx={{
                        // ...Buttons.baseStyle.iconButton,
                        color: ColorsV2.white,
                        fontSize: '0.7em',
                        '&:hover': { color: ColorsV2.acent },
                      }}
                    />
                  </IconButton>
                )}
              </Box>
            </CustomHeaderDiv1>

            <CustomHeaderDiv2 sx={{ justifyContent: 'flex-start', gap: 1, paddingRight: '20px' }}>
              <CustomizedMenus
                flag='messages'
                title1={t('mark_as_read')}
                title2={t('mark_as_unread')}
                title3={t('show_read')}
                title4={t('show_unread')}
                filterMessagesText={t('filter_messages')}
                fetchUnreadMessages={fetchUnreadMessages}
                handleUpdateMessages={handleUpdateMessages}
                setSelected={setSelected}
              />
              <Tooltip title={t('refresh_messages')}>
                <IconButton
                  type='button'
                  sx={{
                    ...Buttons.baseStyle.iconButton,
                    color: ColorsV2.white,
                  }}
                  aria-label='refresh'
                  onClick={() => {
                    setShowFilter(false);
                    setSelected([]);
                    setSelectedRow([]);
                    dispatch(getAllMessages());
                    setPageNo(0);
                    setPageSize(10);
                  }}
                >
                  <RefreshOutlinedIcon />
                </IconButton>
              </Tooltip>

              {selected?.length > 0 && (
                <Tooltip title={t('delete_chosen')}>
                  <IconButton
                    // name='IconButton'
                    onClick={handleMultipleDelete}
                    sx={{
                      ...Buttons.baseStyle.iconButton,
                      boxShadow: 0,
                      color: ColorsV2.white,
                    }}
                  >
                    <Deleteicon />
                  </IconButton>
                </Tooltip>
              )}
              {selected?.length > 0 && (
                <Tooltip title={t('mark_as_read')}>
                  <IconButton
                    onClick={() => {
                      handleUpdateMessages(true);
                    }}
                    sx={{
                      ...Buttons.baseStyle.iconButton,
                      color: ColorsV2.white,
                    }}
                  >
                    <MarkEmailReadIcon />
                  </IconButton>
                </Tooltip>
              )}
              {selected?.length > 0 && (
                <Tooltip title={t('mark_as_unread')}>
                  <IconButton
                    onClick={() => {
                      handleUpdateMessages(false);
                    }}
                    sx={{
                      ...Buttons.baseStyle.iconButton,
                      color: ColorsV2.white,
                    }}
                  >
                    <MarkEmailUnreadOutlinedIcon />
                  </IconButton>
                </Tooltip>
              )}
              {showFilter && (
                <Box
                  sx={
                    {
                      // width: '55%',
                      // currentLang === 'sv' && showFilter ?
                      // : '5px',
                      // marginRight: currentLang === 'sv' && '5px',
                    }
                  }
                >
                  <Badge
                    color='error'
                    badgeContent='X'
                    onClick={() => {
                      setShowFilter(false);
                      setFilter('');
                      setSelected([]);
                      setMlist(storeNotifications);
                    }}
                    sx={{
                      cursor: 'pointer',
                      '& .MuiBadge-badge': { fontSize: '0.5em', height: 13, minWidth: 13 },
                    }}
                  >
                    <FilterDiv
                      sx={{
                        color: ColorsV2.white,
                        border: `2px solid ${ColorsV2.white}`,
                        borderRadius: '4px',
                      }}
                    >
                      <Typography variant='body1'>{filter}</Typography>
                    </FilterDiv>
                  </Badge>
                </Box>
              )}
              {/* <Box
                sx={{
                  color: ColorsV2.white,
                  fontFamily: 'DM Sans',
                  fontStyle: 'normal',
                  fontSize: '0.6em',
                  fontWeight: 400,
                  lineHieght: '21px',
                  // marginRight: selected?.length && showFilter ? '-5vh' : '0px',
                  display: 'flex',
                  justifyContent: currentLang === 'sv' ? 'start' : 'end',
                  width: !showFilter ? '10.8em' : '130px',
                  textAlign: !showFilter && 'right',
                }}
              > */}
              <Typography variant='body1' sx={{ color: ColorsV2.white }}>
                {selected?.length === 0
                  ? ''
                  : `${
                      selected.slice(
                        pageNo === 0 ? 0 : pageSize * pageNo,
                        pageNo === 0 ? pageSize : pageSize * pageNo + pageSize
                      ).length
                    } ${t('selected')}`}
              </Typography>

              {/* </Box> */}
            </CustomHeaderDiv2>
          </CustomDiv>
          {!isLoading && (
            <TableDiv>
              <Box sx={{ flexGrow: 1 }}>
                <CustomizedTable
                  columns={notificationColumns}
                  rows={filteredList ? filteredList : []}
                  rowHeight={70}
                  headerHeight={50}
                  page={pageNo}
                  autoHeight
                  checkboxSelection
                  disableColumnMenu
                  pageSize={pageSize}
                  onPageSizeChange={newPageSize => setPageSize(newPageSize)}
                  onSelectionModelChange={itm => setSelected(itm)}
                  selectionModel={selected}
                  hideFooterSelectedRowCount
                  loading={!mlist?.length && false}
                  sortingOrder={['desc', 'asc']}
                  disableSelectionOnClick
                  localeText={{
                    noRowsLabel: noRowTxt,
                  }}
                  getRowClassName={params => {
                    if (params.row && params.row.id === selectedRow[0]) {
                      return 'selected-row';
                    }
                    return '';
                  }}
                  sx={{
                    '& .MuiDataGrid-row.selected-row': {
                      background: ColorsV2.primary,
                    },
                  }}
                  components={{
                    Footer: props => (
                      <CustomPagination
                        {...props}
                        pageNo={pageNo}
                        setPageNo={setPageNo}
                        count={mlist ? mlist.length : 0}
                        setSelected={setSelected}
                        setPageSize={setPageSize}
                        pageSize={pageSize}
                      />
                    ),
                    NoRowsOverlay: () => (
                      <Stack
                        sx={{
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          height: '80%',
                          fontSize: '0.8em',
                        }}
                      >
                        {noRowTxt}
                      </Stack>
                    ),
                  }}
                  getRowHeight={({ id, densityFactor }) => {
                    if (selectedRow.includes(id)) {
                      return messageBodies[id]?.length < 100
                        ? 200
                        : messageBodies[id]?.length < 200
                        ? 250
                        : messageBodies[id]?.length < 300
                        ? 300
                        : 450 * densityFactor;
                    }

                    return null;
                  }}
                  ref={gridRef}
                />
              </Box>
            </TableDiv>
          )}
        </TableHeader>

        {isLoading && <Loader />}
      </ParentDiv>
  );
};

export default NotificationList;
